/** @format */

import { useEffect, useMemo, useState } from 'react';
import {
  fetchTrainingPreviewImage,
  getCachedTrainingPreviewImages
} from 'src/utils/academyTrainingImages';

interface PreviewImageState {
  url: string | null;
}

const buildResolvedState = (imageUrl?: string | null): PreviewImageState => ({ url: imageUrl || null });

export default function useTrainingPreviewImages(
  trainingIds?: Array<string | number | null | undefined>
): Record<string, PreviewImageState> {
  const [previewImageStates, setPreviewImageStates] = useState<Record<string, PreviewImageState>>({});
  const uniqueTrainingIds = useMemo(
    () => [...new Set((trainingIds || []).filter((id): id is string | number => Boolean(id)))],
    [trainingIds]
  );
  const trainingIdsKey = uniqueTrainingIds.join(',');

  useEffect(() => {
    if (!uniqueTrainingIds.length) {
      setPreviewImageStates({});
      return undefined;
    }

    const cachedImages = getCachedTrainingPreviewImages(uniqueTrainingIds);
    const initialStates = uniqueTrainingIds.reduce<Record<string, PreviewImageState>>((states, trainingId) => {
      if (cachedImages[trainingId]) {
        states[trainingId] = buildResolvedState(cachedImages[trainingId]);
      }
      return states;
    }, {});
    setPreviewImageStates(initialStates);

    let cancelled = false;

    const loadPreviewImagesSequentially = async () => {
      for (const trainingId of uniqueTrainingIds) {
        if (cancelled) return;
        if (cachedImages[trainingId]) continue;

        try {
          const imageUrl = await fetchTrainingPreviewImage(trainingId);
          if (!cancelled) {
            setPreviewImageStates(prev => ({
              ...prev,
              [trainingId]: buildResolvedState(imageUrl)
            }));
          }
        } catch {
          if (!cancelled) {
            setPreviewImageStates(prev => ({
              ...prev,
              [trainingId]: buildResolvedState(null)
            }));
          }
        }
      }
    };

    loadPreviewImagesSequentially();

    return () => {
      cancelled = true;
    };
  }, [trainingIdsKey, uniqueTrainingIds]);

  return previewImageStates;
}
